import { Injectable } from '@angular/core';
import { distinctUntilChanged, Observable } from 'rxjs';
import { ObjectUtils } from '../utils/object.utils';

const timeoutDefault = 30000;

@Injectable()
export class GeolocationService {
  getCurrentLocation(options?: PositionOptions) {
    return new Observable<GeolocationPosition>((subscriber) => {
      if ('geolocation' in navigator) {
        options = Object.assign<PositionOptions, PositionOptions>(
          {
            enableHighAccuracy: true,
            maximumAge: 30000,
            timeout: timeoutDefault,
          },
          options ?? {}
        );

        navigator.geolocation.getCurrentPosition(
          (position) => {
            subscriber.next(position);
            subscriber.complete();
          },
          (error) => {
            subscriber.error(error);
          },
          options
        );
      } else {
        subscriber.error(new Error('Geolocation is not available!'));
      }
    });
  }

  watchCurrentLocation(options?: PositionOptions) {
    return new Observable<GeolocationPosition>((subscriber) => {
      if ('geolocation' in navigator) {
        options = Object.assign<PositionOptions, PositionOptions>(
          {
            enableHighAccuracy: true,
            maximumAge: 30000,
            timeout: timeoutDefault,
          },
          options
        );

        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            subscriber.next(position);
          },
          (error) => {
            subscriber.error(error);
          },
          options
        );

        subscriber.add(() => {
          navigator.geolocation.clearWatch(watchId);
        });
      } else {
        subscriber.error(new Error('Geolocation is not available!'));
      }
    }).pipe(
      distinctUntilChanged((a, b) => {
        return ObjectUtils.equals(
          coordsToPlain(a?.coords),
          coordsToPlain(b?.coords)
        );
      })
    );
  }
}

function coordsToPlain(coords: GeolocationCoordinates) {
  if (!coords) {
    return null;
  }

  return {
    accuracy: coords.accuracy,
    altitude: coords.altitude,
    altitudeAccuracy: coords.altitudeAccuracy,
    heading: coords.heading,
    speed: coords.speed,
    latitude: coords.latitude,
    longitude: coords.longitude,
  };
}
